import React, { useState, useEffect, useRef, useMemo } from 'react';
import { message as antMessage, Spin, Button, Badge } from 'antd';

// Import all component types
import MasterDetailTable from './MasterDetailTable';
import MarkdownText from './MarkdownText';
import ListDetailTables from './ListDetailTables';
import DecisionTree from './DecisionTree';
import StepsComponent from './StepsComponent';
import BarChartComponent from './BarChartComponent';
import LineChartComponent from './LineChartComponent';
import PieChartComponent from './PieChartComponent';
import BubbleChartComponent from './BubbleChartComponent';
import ScatterChartComponent from './ScatterChartComponent';
import SearchResults from './SearchResults';
import AnnotatedDocument from './AnnotatedDocument';
import ListComponent from './ListComponent';
import ChatComponentV2 from './ChatComponentV2';
import ActionCenter from './ActionCenter';
import HeaderActionCenter from './HeaderActionCenter';
import InputSelectableList from './InputSelectableList';
import InputEditableList from './InputEditableList';
import SimpleTableDisplay from './SimpleTableDisplay';
import AccordionTableDisplay from './AccordionTableDisplay';
import FinOpsTableComponent from './FinOpsTableComponent';
import AnnotatedPDFViewer from './AnnotatedPDFViewer';
import ReviewPanel from './ReviewPanel';
//import KnowledgeGraphComponent from './KnowledgeGraphComponent';
import AdvancedTableDisplay from './AdvancedTableDisplay';


/**
 * DraggableLayoutManagerV3 - Generic Component Layout Manager
 * 
 * Key differences from V2:
 * - No hardcoded chat component handling - all components are treated equally
 * - Order-based column assignment: position in response determines column placement
 * - Truly generic - can be used for any agent type
 * - Column assignment:
 *   - 1 column: All components stacked vertically
 *   - 2 columns: 1st component → Col 1, rest → Col 2
 *   - 3 columns: 1st → Col 1, 2nd → Col 2, 3rd → Col 3, then cycle
 */
const DraggableLayoutManagerV3 = ({ 
  apiData, 
  layout, 
  onVersionChange,
  sessionId,
  activeAgent,
  currentWorkflowState,
  username = 'testuser',
  onComponentUpdate,
  projectId,
  progressData,
  isProcessing,
  supportsProgressTracking,  
  onProcessingStart,  
  onProcessingEnd,
  agentId       
}) => {
  // State for tracking components and their active versions
  const [componentVersions, setComponentVersions] = useState({});
  const [componentDisplay, setComponentDisplay] = useState({});
  const [componentsToDisplay, setComponentsToDisplay] = useState([]);
  
  // State for tracking component registry for updates
  const [componentRegistry, setComponentRegistry] = useState({});
  const [allComponentIds, setAllComponentIds] = useState([]);
  const [componentTypes, setComponentTypes] = useState({});
  
  // State for resizable columns
  const [leftColumnWidth, setLeftColumnWidth] = useState(33);
  
  // Remember the components registered already
  const registeredComponentsRef = useRef(new Set());

  // Track the previous apiData for comparison
  const prevApiDataRef = useRef(null);
  
  const [chatLoading, setChatLoading] = useState(false);
  const [evaluationResult, setEvaluationResult] = useState(null);

  // Determine column count based on agent configuration or layout prop
  const columnCount = useMemo(() => {
    if (typeof layout === 'number') {
      if (layout === 1) return 1;
      if (layout === 3) return 3;
      return 2;
    }
    
    if (activeAgent && activeAgent.layout_columns) {
      if (activeAgent.layout_columns === 1) return 1;
      if (activeAgent.layout_columns === 3) return 3;
      return 2;
    }
    
    return 2;
  }, [layout, activeAgent]);

  // Set initial column widths based on column count
  useEffect(() => {
    if (columnCount === 3) {
      setLeftColumnWidth(33);
    } else if (columnCount === 2) {
      setLeftColumnWidth(50);
    } else {
      setLeftColumnWidth(100);
    }
  }, [columnCount]);

  // Render loading state
  const renderLoadingState = () => {
    if (!apiData && !isProcessing) {
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>Loading components...</div>
        </div>
      );
    }

    if (isProcessing || (!apiData && supportsProgressTracking)) {
      if (supportsProgressTracking && progressData) {
        return (
          <div style={{ 
            padding: '40px', 
            textAlign: 'center',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            <Spin size="large" />
            <div style={{ marginTop: '24px' }}>
              <h3>Processing {activeAgent?.name || 'Agent'}</h3>
              <div style={{ marginBottom: '16px' }}>
                <strong>{progressData.step_name}</strong>
              </div>
              <div style={{ 
                width: '100%', 
                backgroundColor: '#f0f0f0', 
                borderRadius: '10px',
                marginBottom: '8px'
              }}>
                <div style={{
                  width: `${progressData.percentage}%`,
                  backgroundColor: '#6200EE',
                  height: '8px',
                  borderRadius: '10px',
                  transition: 'width 0.3s ease'
                }} />
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>
                Step {progressData.current_step} of {progressData.total_steps} ({progressData.percentage}%)
              </div>
            </div>
          </div>
        );
      } else {
        return (
          <div style={{ 
            padding: '40px', 
            textAlign: 'center',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            <Spin size="large" />
            <div style={{ marginTop: '24px' }}>
              <h3>Processing {activeAgent?.name || 'Agent'}</h3>
              <div style={{ fontSize: '16px', color: '#666', marginTop: '12px' }}>
                {supportsProgressTracking ? 'Initializing...' : 'Loading data...'}
              </div>
            </div>
          </div>
        );
      }
    }

    return null;
  };

  // Process API data when it changes
  useEffect(() => {
    if (!apiData) return;
    
    console.log("Processing updated apiData:", Object.keys(apiData));
    
    try {
      const componentIds = [];
      const typesMap = {};
      
      Object.entries(apiData).forEach(([key, componentData]) => {
        if (componentData && typeof componentData === 'object' && 
            componentData.uuid && componentData.type) {
          componentIds.push(componentData.uuid);
          typesMap[componentData.uuid] = {
            type: componentData.type,
            title: key
          };
        }
      });
      
      setAllComponentIds(componentIds);
      setComponentTypes(typesMap);
      prevApiDataRef.current = apiData;    
      
    } catch (error) {
      console.error("Error processing components:", error);
    }
  }, [apiData]);

  // Update display settings for new components
  useEffect(() => {
    if (!apiData) return;
    
    const updatedSettings = {...componentDisplay};
    let hasChanges = false;
    
    Object.entries(apiData).forEach(([key, componentData]) => {
      // In V3, we don't skip any component type - all are treated equally
      if (updatedSettings[key] === undefined) {
        updatedSettings[key] = true;
        hasChanges = true;
      }
    });
    
    if (hasChanges) {
      setComponentDisplay(updatedSettings);
    }
  }, [apiData]);

  // Update the list of components to display
  useEffect(() => {
    if (apiData) {
      const newComponentsToDisplay = Object.entries(apiData)
        .filter(([key, comp]) => {
          return comp && 
                typeof comp === 'object' &&
                comp?.uuid &&
                comp?.type &&
                comp?.data &&
                componentDisplay[key];
        })
        .map(([key]) => key);
        
      const currentDisplayed = componentsToDisplay.map(c => c);
      
      if (JSON.stringify(newComponentsToDisplay) !== JSON.stringify(currentDisplayed)) {
        setComponentsToDisplay(newComponentsToDisplay);
      }
    }
  }, [componentDisplay, apiData]);

  // Register a component with the registry
  const registerComponent = (componentId, refreshCallback) => {
    if (!componentId || !refreshCallback) return () => {};
    
    if (!registeredComponentsRef.current.has(componentId)) {
      console.log(`Registering component: ${componentId}`);
      registeredComponentsRef.current.add(componentId);
      
      setComponentRegistry(prev => ({
        ...prev,
        [componentId]: refreshCallback
      }));
    }
    
    return () => {
      registeredComponentsRef.current.delete(componentId);
      setComponentRegistry(prev => {
        const newRegistry = { ...prev };
        delete newRegistry[componentId];
        return newRegistry;
      });
    };
  };

  // Notify specific components to update
  const notifyComponentUpdates = (componentIds) => {
    if (!componentIds || !Array.isArray(componentIds)) return;
    
    console.log("Notifying components to update:", componentIds);
    componentIds.forEach(id => {
      if (componentRegistry[id]) {
        componentRegistry[id]();
      }
    });
  };

  // Handle component click for version changes
  const handleComponentClick = (componentRef) => {
    if (!componentRef) return;
    
    try {
      const title = componentRef.title || componentRef.componentKey;
      const uuid = componentRef.uuid;
      const requestedVersion = componentRef.version || 1;
      
      if (!uuid) {
        antMessage.error("Invalid component reference");
        return;
      }
      
      if (onVersionChange) {
        antMessage.info(`Loading ${title} (version ${requestedVersion})...`);
        onVersionChange(uuid, requestedVersion);
      }
    } catch (error) {
      console.error("Error handling component click:", error);
      antMessage.error("Failed to show component");
    }
  };

  // Memoized component creation
  const memoizedCreateComponent = useMemo(() => {
    const componentCache = {};
    
    return (key, componentData, onVersionChange) => {
      const cacheKey = `${key}-${componentData?.uuid}-${componentData?.current_version}`;
      
      if (componentCache[cacheKey] && 
          JSON.stringify(componentCache[cacheKey].data) === JSON.stringify(componentData?.data)) {
        return componentCache[cacheKey].component;
      }
      
      const component = createComponent(key, componentData, onVersionChange);
      
      componentCache[cacheKey] = {
        component,
        data: componentData?.data
      };
      
      return component;
    };
  }, []);

  // Handle input submission (for chat component if present)
  const handleInputSubmit = async (inputData) => {
    try {
      const { inputId, inputConfig, value, messageIndex, uuid, sessionId: inputSessionId, agent_key, project_id } = inputData;

      if (onProcessingStart) {
        onProcessingStart();
      }

      const requestBody = {
        inputResponse: {
          inputId: inputId,
          value: value,
          displayText: `Selected: ${value}`,
          messageIndex: messageIndex
        },
        project_id: project_id,
        agent_id: agentId
      };

      const endpoint = `${activeAgent?.endpoint}/chat`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId}`
        },
        body: JSON.stringify(requestBody),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Input submission failed: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.updatedComponentIds && result.updatedComponentIds.length > 0) {
        notifyComponentUpdates(result.updatedComponentIds);
      }
      
      if (result) {
        handleComponentUpdate(result);
      }

      return true;
    } catch (error) {
      console.error('Error submitting input:', error);
      antMessage.error(`Failed to submit input: ${error.message}`);
      return false;
    } finally {
      if (onProcessingEnd) {
        onProcessingEnd();
      }
    }
  };

  // Handle chat messages (if chat component is present)
  const handleChatMessage = async (message, chatUuid, notifyComponentRefresh, agentKey, projectIdParam) => {
    try {
      setChatLoading(true);
      
      if (onProcessingStart) {
        onProcessingStart();
      }

      // This would need to be implemented if chat functionality is needed
      // For now, return false as V3 is designed for non-chat use cases
      console.log("Chat message handling - implement if needed");
      return false;
    } catch (error) {
      console.error("Error processing chat message:", error);
      antMessage.error(`Error: ${error.message}`);
      return false;
    } finally {
      setTimeout(() => {
        setChatLoading(false);
      }, 300);

      if (onProcessingEnd) {
        onProcessingEnd();
      }
    }
  };

  // Handle file uploads
  const handleFileUpload = async (files, chatUuid, context, sessionIdParam) => {
    try {
      if (onProcessingStart) {
        onProcessingStart();
      }

      const endpoint = context.endpoint || `${activeAgent?.endpoint}/upload`;
      
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });
      
      formData.append('chatUuid', chatUuid);
      if (context.documentType) formData.append('documentType', context.documentType);
      if (context.requestId) formData.append('requestId', context.requestId);
      if (context.metadata) formData.append('metadata', JSON.stringify(context.metadata));
      if (context.fileMetadata) formData.append('fileMetadata', JSON.stringify(context.fileMetadata));
      formData.append('context', JSON.stringify(context));
      if (currentWorkflowState?.agent_key) {
        formData.append('agent_key', currentWorkflowState.agent_key);
      }
      if (context.component_map || currentWorkflowState?.component_map) {
        const componentMap = context.component_map || currentWorkflowState?.component_map;
        formData.append('component_map', JSON.stringify(componentMap));
      }
      formData.append('agent_id', agentId);

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionIdParam}`
        },
        body: formData,
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.updatedComponentIds && result.updatedComponentIds.length > 0) {
        notifyComponentUpdates(result.updatedComponentIds);
      }
      
      if (result) {
        handleComponentUpdate(result);
      }
      
      antMessage.success(`${files.length} file(s) uploaded successfully`);
      return true;
    } catch (error) {
      console.error("Error uploading files:", error);
      antMessage.error("Failed to upload files");
      return false;
    } finally {
      if (onProcessingEnd) {
        onProcessingEnd();
      }      
    }
  };

  // Handle component updates
  const handleComponentUpdate = (result) => {
    if (!result) {
      console.warn('Empty result received in handleComponentUpdate');
      return;
    }
    
    console.log('Component update result received:', result);
    
    if (result.evaluationResult !== undefined) {
      if (typeof setEvaluationResult === 'function') {
        setEvaluationResult(result.evaluationResult);
      }
    }
    
    if (result.updatedComponentIds && result.updatedComponentIds.length > 0) {
      console.log(`Components updated: ${result.updatedComponentIds.join(', ')}`);
      if (typeof notifyComponentUpdates === 'function') {
        notifyComponentUpdates(result.updatedComponentIds);
      }
    }
    
    if (onComponentUpdate && typeof onComponentUpdate === 'function') {
      onComponentUpdate(result);
    }
  };

  // Create a component based on its type
  const createComponent = (key, componentData, onVersionChangeParam) => {
    if (!componentData) {
      console.error(`Missing component data for key: ${key}`);
      return null;
    }
    
    console.log(`Creating component for ${key}:`, componentData.type);
    
    const props = {
      data: componentData.data,
      title: key,
      uuid: componentData.uuid || '',
      currentVersion: componentData.current_version || 1,
      versionInfo: componentData.version_info || {},
      onVersionChange: onVersionChangeParam,
      registerComponent: componentData.uuid ? (callback) => registerComponent(componentData.uuid, callback) : null
    };
    
    try {
      switch (componentData.type) {
        case 'master_detail_table':
          return <MasterDetailTable {...props} />;
        
        case 'markdown_text':
          return <MarkdownText {...props} />;
        
        case 'list_detail_tables':
          return <ListDetailTables {...props} />;
        
        case 'decision_tree':
          return <DecisionTree {...props} />;
        
        case 'process_steps':
          return <StepsComponent {...props} />;
        
        case 'bar_chart':
          return <BarChartComponent {...props} />;
        
        case 'line_chart':
          return <LineChartComponent {...props} />;
        
        case 'pie_chart':
          return <PieChartComponent {...props} />;
        
        case 'bubble_chart':
          return <BubbleChartComponent {...props} />;
        
        case 'scatter_chart':
          return <ScatterChartComponent {...props} />;
        
        case 'search_results':
          return <SearchResults {...props} />;
        
        case 'finops_table':
          return <FinOpsTableComponent {...props} />;
        
        case 'annotated_document':
          return <AnnotatedDocument {...props} />;

        case 'advanced_table':
          return <AdvancedTableDisplay {...props} />;          

        case 'list_component':
          return <ListComponent {...props} />;
        
        case 'chat_component':
          return (
            <ChatComponentV2 
              {...props} 
              isLoading={chatLoading}
              onSendMessage={(message, chatUuid) => handleChatMessage(message, chatUuid, notifyComponentUpdates, currentWorkflowState?.agent_key)}
              onFileUpload={handleFileUpload}
              onComponentClick={handleComponentClick}
              onInputSubmit={handleInputSubmit}
              sessionId={sessionId}
              agent_key={currentWorkflowState?.agent_key}
              project_id={projectId}
              component_map={currentWorkflowState?.component_map}
            />
          );
        
        case 'input_selectable_list':
          return (
            <InputSelectableList 
              {...props}
              sessionId={sessionId}
              endpointUrl={`${activeAgent?.endpoint || ''}/api/list/selectable`}
              onComponentUpdate={onComponentUpdate}
              project_id={projectId}
              agent_key={currentWorkflowState?.agent_key}
              onProcessingStart={onProcessingStart}
              onProcessingEnd={onProcessingEnd}
            />
          );
        
        case 'input_editable_list':
          return (
            <InputEditableList
              {...props}
              sessionId={sessionId}
              endpointUrl={`${activeAgent?.endpoint || ''}/api/list/editable`}
              project_id={projectId}
              agent_key={currentWorkflowState?.agent_key}
              onProcessingStart={onProcessingStart}
              onProcessingEnd={onProcessingEnd}
            />
          );
        
        case 'simple_table_display':
          return (
            <SimpleTableDisplay
              {...props}
              sessionId={sessionId}
              project_id={projectId}
              agent_key={currentWorkflowState?.agent_key}
            />
          );
        
        case 'action_center':
          return (
            <ActionCenter 
              {...props} 
              activeAgent={activeAgent}
              componentIds={allComponentIds}
              componentTypes={componentTypes}
              sessionId={sessionId}
              onActionComplete={(action, data) => {
                if (data && data.updatedComponentIds) {
                  notifyComponentUpdates(data.updatedComponentIds);
                }
              }}
              project_id={projectId}
            />
          );
        
        case 'review_panel':
          return (
            <ReviewPanel 
              {...props}
              sessionId={sessionId}
              activeAgent={activeAgent}
              onDetailRequest={(item, result) => {
                // Handle detail requests - this triggers when info button is clicked
              }}
              onComponentUpdate={onComponentUpdate}
              project_id={projectId}
              agent_key={currentWorkflowState?.agent_key}
            />
          );
        
        case 'accordion_table_display':
          return (
            <AccordionTableDisplay
              {...props}
              sessionId={sessionId}
              project_id={projectId}
              agent_key={currentWorkflowState?.agent_key}
            />
          );
        
        case 'annotated_pdf':
          return (
            <AnnotatedPDFViewer
              {...props}
              sessionId={sessionId}
              project_id={projectId}
              agent_key={currentWorkflowState?.agent_key}
            />
          );
          
        default:
          console.error('Unknown component type:', componentData.type);
          return (
            <div style={{ 
              padding: '16px', 
              backgroundColor: '#fff3cd', 
              border: '1px solid #ffc107',
              borderRadius: '4px',
              marginBottom: '16px'
            }}>
              <strong>Unknown component type:</strong> {componentData.type}
              <br />
              <small>Key: {key}</small>
            </div>
          );
      }
    } catch (error) {
      console.error('Error creating component:', error);
      return null;
    }
  };

  // Handle resize for the splitter
  const handleResize = (newPercentage) => {
    const constrainedWidth = Math.min(Math.max(newPercentage, 20), 80);
    setLeftColumnWidth(constrainedWidth);
  };

  // Toggle component display
  const toggleComponent = (key) => {
    setComponentDisplay(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // If no apiData, show loading state
  if (!apiData) {
    return renderLoadingState();
  }

  // Get all component keys (no special handling for any type)
  const componentKeys = Object.keys(apiData).filter(key => {
    const item = apiData[key];
    return item && 
           typeof item === 'object' &&
           item.uuid &&
           item.type &&
           item.data;
  });

  // Calculate column widths
  const rightColumnWidth = 100 - leftColumnWidth;
  const col2Width = columnCount === 3 ? rightColumnWidth / 2 : rightColumnWidth;
  const col3Width = columnCount === 3 ? rightColumnWidth / 2 : 0;

  /**
   * Get components for a specific column based on ORDER in response
   * - 1 column: All components in column 1
   * - 2 columns: 1st component → Col 1, rest → Col 2
   * - 3 columns: 1st → Col 1, 2nd → Col 2, 3rd → Col 3
   */
  const getComponentsForColumn = (columnIndex) => {
    const visibleComponents = componentsToDisplay.filter(key => apiData[key] && apiData[key].data);
    
    if (columnCount === 1) {
      // Single column: all components stacked
      if (columnIndex !== 1) return [];
      
      return visibleComponents.map(key => (
        <div key={key} style={{ marginBottom: '16px', position: 'relative' }}>
          {memoizedCreateComponent(key, apiData[key], onVersionChange)}
          <div style={{ 
            position: 'absolute', 
            top: '4px', 
            right: '4px',
            zIndex: 1000
          }}>
            <Button 
              size="small" 
              shape="circle" 
              type="text"
              onClick={() => toggleComponent(key)}
              title="Hide component"
            >
              ✕
            </Button>
          </div>
        </div>
      ));
    }
    
    if (columnCount === 2) {
      // Two columns: 1st component → Col 1, rest → Col 2
      if (columnIndex === 1) {
        // Column 1 gets the first component
        if (visibleComponents.length > 0) {
          const key = visibleComponents[0];
          return [(
            <div key={key} style={{ marginBottom: '16px', position: 'relative' }}>
              {memoizedCreateComponent(key, apiData[key], onVersionChange)}
              <div style={{ 
                position: 'absolute', 
                top: '4px', 
                right: '4px',
                zIndex: 1000
              }}>
                <Button 
                  size="small" 
                  shape="circle" 
                  type="text"
                  onClick={() => toggleComponent(key)}
                  title="Hide component"
                >
                  ✕
                </Button>
              </div>
            </div>
          )];
        }
        return [];
      }
      
      if (columnIndex === 2) {
        // Column 2 gets all remaining components
        return visibleComponents.slice(1).map(key => (
          <div key={key} style={{ marginBottom: '16px', position: 'relative' }}>
            {memoizedCreateComponent(key, apiData[key], onVersionChange)}
            <div style={{ 
              position: 'absolute', 
              top: '4px', 
              right: '4px',
              zIndex: 1000
            }}>
              <Button 
                size="small" 
                shape="circle" 
                type="text"
                onClick={() => toggleComponent(key)}
                title="Hide component"
              >
                ✕
              </Button>
            </div>
          </div>
        ));
      }
    }
    
    if (columnCount === 3) {
      // Three columns: direct assignment by position
      // Index 0 → Col 1, Index 1 → Col 2, Index 2 → Col 3
      // Index 3 → Col 1, Index 4 → Col 2, Index 5 → Col 3, etc.
      const componentsForColumn = visibleComponents.filter((_, index) => {
        if (columnIndex === 1) return index % 3 === 0;
        if (columnIndex === 2) return index % 3 === 1;
        if (columnIndex === 3) return index % 3 === 2;
        return false;
      });
      
      return componentsForColumn.map(key => (
        <div key={key} style={{ marginBottom: '16px', position: 'relative', maxHeight: 'calc(100vh - 300px)', overflow: 'auto' }}>
          {memoizedCreateComponent(key, apiData[key], onVersionChange)}
          <div style={{ 
            position: 'absolute', 
            top: '4px', 
            right: '4px',
            zIndex: 1000
          }}>
            <Button 
              size="small" 
              shape="circle" 
              type="text"
              onClick={() => toggleComponent(key)}
              title="Hide component"
            >
              ✕
            </Button>
          </div>
        </div>
      ));
    }
    
    return [];
  };

  // Render empty column placeholder
  const renderEmptyColumn = (columnNumber) => (
    <div style={{ 
      padding: '10px', 
      textAlign: 'center', 
      color: '#999',
      backgroundColor: '#f5f5f5',
      borderRadius: '4px',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div>
        <p>No components in column {columnNumber}</p>
        {componentKeys.length > 0 && componentKeys.some(key => !componentDisplay[key]) && (
          <div>
            <p>Hidden components:</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px' }}>
              {componentKeys
                .filter(key => !componentDisplay[key])
                .map(key => (
                  <Button 
                    key={key}
                    onClick={() => toggleComponent(key)}
                    size="small"
                    type="primary"
                    ghost
                  >
                    {key}
                  </Button>
                ))
              }
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Header with outcome text and action buttons */}
      {activeAgent && (
        <HeaderActionCenter
          activeAgent={activeAgent}
          componentIds={allComponentIds}
          componentTypes={componentTypes}
          sessionId={sessionId}
          onActionComplete={(action, data) => {
            if (data && data.updatedComponentIds) {
              notifyComponentUpdates(data.updatedComponentIds);
            }
          }}
        />
      )}
      
      {/* Main resizable layout */}
      <div className="resizable-container" style={{ 
        display: 'flex', 
        top: 0,
        height: 'auto',
        minHeight: '600px',
        maxHeight: 'calc(100vh - 150px)',
        width: '100%',
        overflow: 'hidden',
        backgroundColor: '#f0f2f5',
        border: '1px solid #f0f0f0',
        borderRadius: '4px',
        position: 'relative',
        marginBottom: '0',
      }}>
        {/* Single column layout */}
        {columnCount === 1 ? (
          <div style={{ 
            width: '100%', 
            padding: '8px',
            overflow: 'auto',
            maxHeight: 'calc(100vh - 200px)'
          }}>
            {getComponentsForColumn(1)}
          </div>
        ) : (
          <>
            {/* Column 1 */}
            <div 
              style={{ 
                width: `${leftColumnWidth}%`, 
                padding: '8px',
                overflow: 'auto',
                borderRight: '1px solid #e8e8e8',
                position: 'relative',
                height: '100%',
                maxHeight: 'calc(100vh - 200px)' 
              }}
            >
              {getComponentsForColumn(1).length > 0 ? (
                getComponentsForColumn(1)
              ) : (
                renderEmptyColumn(1)
              )}
            </div>
            
            {/* Resize handle between column 1 and 2 */}
            <div 
              style={{
                position: 'absolute',
                top: 0,
                left: `${leftColumnWidth}%`,
                width: '8px',
                height: '100%',
                background: '#e1e1e1',
                cursor: 'col-resize',
                zIndex: 10,
                transform: 'translateX(-50%)',
                transition: 'background 0.2s'
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                
                const startX = e.clientX;
                const startWidth = leftColumnWidth;
                
                e.target.style.background = '#6200EE';
                document.body.style.cursor = 'col-resize';
                
                const onMouseMove = (moveEvent) => {
                  const containerWidth = document.querySelector('.resizable-container').offsetWidth;
                  const delta = moveEvent.clientX - startX;
                  const newWidthPx = (startWidth / 100) * containerWidth + delta;
                  const newWidthPercent = (newWidthPx / containerWidth) * 100;
                  
                  handleResize(newWidthPercent);
                };
                
                const onMouseUp = () => {
                  e.target.style.background = '#e1e1e1';
                  document.body.style.cursor = 'default';
                  
                  document.removeEventListener('mousemove', onMouseMove);
                  document.removeEventListener('mouseup', onMouseUp);
                };
                
                document.addEventListener('mousemove', onMouseMove);
                document.addEventListener('mouseup', onMouseUp);
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#c0c0c0';
              }}
              onMouseOut={(e) => {
                e.target.style.background = '#e1e1e1';
              }}
            />
            
            {/* Column 2 */}
            <div 
              style={{ 
                width: `${col2Width}%`, 
                padding: '8px', 
                overflow: 'auto',
                borderRight: columnCount === 3 ? '1px solid #e8e8e8' : 'none',
                maxHeight: 'calc(100vh - 250px)'
              }}
            >
              {getComponentsForColumn(2).length > 0 ? (
                getComponentsForColumn(2)
              ) : (
                renderEmptyColumn(2)
              )}
            </div>
            
            {/* Column 3 (only in 3-column layout) */}
            {columnCount === 3 && (
              <>
                {/* Resize handle between column 2 and 3 */}
                <div 
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: `${leftColumnWidth + col2Width}%`,
                    width: '8px',
                    height: '100%',
                    background: '#e1e1e1',
                    cursor: 'col-resize',
                    zIndex: 10,
                    transform: 'translateX(-50%)',
                    transition: 'background 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.background = '#c0c0c0';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = '#e1e1e1';
                  }}
                />
                
                {/* Column 3 content */}
                <div 
                  style={{ 
                    width: `${col3Width}%`, 
                    padding: '8px', 
                    overflow: 'auto',
                    maxHeight: 'calc(100vh - 250px)'
                  }}
                >
                  {getComponentsForColumn(3).length > 0 ? (
                    getComponentsForColumn(3)
                  ) : (
                    renderEmptyColumn(3)
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default DraggableLayoutManagerV3;